import { Fragment, useState } from 'react'
import { Link } from 'react-router-dom'
import { ProjectElementType, UserRole } from '@kfolks/types'
import { roleAtLeast } from '@/auth'
import { useClients, useCreateProject, useUpdateProject, useDeleteProject } from '@/hooks/useClients'
import { useAuth } from '@/hooks/useAuth'
import { useFilteredQuery } from '@/hooks/useFilteredQuery'
import {
	useProjectElements,
	useCreateProjectElement,
	useUpdateProjectElement,
	useDeleteProjectElement,
} from '@/hooks/useProjectElements'
import type { ProjectElementFormData } from '@/hooks/useProjectElements'
import { FilterBar } from '@/components/FilterBar'
import type { FilterDefinition } from '@/components/FilterBar'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { EmailAutocomplete } from '@/components/EmailAutocomplete'

const ELEMENT_TYPE_LABELS: Record<string, string> = {
	[ProjectElementType.GITHUB_REPO]: 'GitHub Repo',
	[ProjectElementType.DATABASE]: 'Database',
	[ProjectElementType.CLOUD_INFRASTRUCTURE]: 'Cloud Infrastructure',
	[ProjectElementType.CI_CD_PIPELINE]: 'CI/CD Pipeline',
	[ProjectElementType.DOMAIN]: 'Domain',
	[ProjectElementType.MONITORING]: 'Monitoring',
	[ProjectElementType.DOCUMENTATION]: 'Documentation',
	[ProjectElementType.COMMUNICATION_CHANNEL]: 'Communication',
	[ProjectElementType.STORAGE]: 'Storage',
	[ProjectElementType.API]: 'API',
	[ProjectElementType.OTHER]: 'Other',
}

const EMPTY_ELEMENT: ProjectElementFormData = {
	elementType: ProjectElementType.GITHUB_REPO,
	name: '',
	description: '',
	environment: '',
	url: '',
}

function ProjectElementsSection({ projectId, isSuperAdmin }: { projectId: string; isSuperAdmin: boolean }) {
	const { data: elements = [], isLoading } = useProjectElements(projectId)
	const createMutation = useCreateProjectElement()
	const updateMutation = useUpdateProjectElement()
	const deleteMutation = useDeleteProjectElement()

	const [showAddForm, setShowAddForm] = useState(false)
	const [formData, setFormData] = useState<ProjectElementFormData>(EMPTY_ELEMENT)
	const [editingId, setEditingId] = useState<string | null>(null)

	const resetForm = () => {
		setFormData(EMPTY_ELEMENT)
		setShowAddForm(false)
		setEditingId(null)
	}

	const handleCreate = async () => {
		if (!formData.name) return
		await createMutation.mutateAsync({ projectId, data: formData })
		resetForm()
	}

	const handleUpdate = async () => {
		if (!editingId || !formData.name) return
		await updateMutation.mutateAsync({ projectId, elementId: editingId, data: formData })
		resetForm()
	}

	const handleDelete = async (elementId: string) => {
		if (!confirm('Delete this element?')) return
		await deleteMutation.mutateAsync({ projectId, elementId })
	}

	if (isLoading) return <div className="py-2 text-sm text-gray-500">Loading elements...</div>

	const grouped = elements.reduce(
		(acc, el) => {
			const type = el.elementType
			if (!acc[type]) acc[type] = []
			acc[type].push(el)
			return acc
		},
		{} as Record<string, typeof elements>,
	)

	return (
		<div className="bg-gray-50 p-4 border-t">
			<div className="flex justify-between items-center mb-3">
				<h4 className="text-sm font-semibold text-gray-700">Project Elements</h4>
				{isSuperAdmin && !showAddForm && (
					<Button onClick={() => setShowAddForm(true)} size="sm" variant="outline" className="h-7 px-2 text-xs">
						Add Element
					</Button>
				)}
			</div>

			{showAddForm && (
				<div className="bg-white rounded border p-3 mb-3 space-y-2">
					<div className="grid grid-cols-2 gap-2">
						<select
							value={formData.elementType}
							onChange={(e) => setFormData({ ...formData, elementType: e.target.value })}
							className="border rounded px-2 py-1 text-sm"
						>
							{Object.entries(ELEMENT_TYPE_LABELS).map(([value, label]) => (
								<option key={value} value={value}>
									{label}
								</option>
							))}
						</select>
						<Input
							value={formData.name}
							onChange={(e) => setFormData({ ...formData, name: e.target.value })}
							placeholder="Name"
							className="h-8 text-sm"
						/>
					</div>
					<Input
						value={formData.description ?? ''}
						onChange={(e) => setFormData({ ...formData, description: e.target.value })}
						placeholder="Description"
						className="h-8 text-sm"
					/>
					<div className="grid grid-cols-2 gap-2">
						<Input
							value={formData.environment ?? ''}
							onChange={(e) => setFormData({ ...formData, environment: e.target.value })}
							placeholder="Environment (e.g. prod)"
							className="h-8 text-sm"
						/>
						<Input
							value={formData.url ?? ''}
							onChange={(e) => setFormData({ ...formData, url: e.target.value })}
							placeholder="URL"
							className="h-8 text-sm"
						/>
					</div>
					<div className="flex gap-1">
						<Button
							onClick={() => void handleCreate()}
							size="sm"
							disabled={createMutation.isPending}
							className="h-7 px-2 text-xs"
						>
							{createMutation.isPending ? 'Adding...' : 'Add'}
						</Button>
						<Button onClick={resetForm} size="sm" variant="outline" className="h-7 px-2 text-xs">
							Cancel
						</Button>
					</div>
				</div>
			)}

			{elements.length === 0 && !showAddForm && <p className="text-sm text-gray-400">No elements yet.</p>}

			{Object.entries(grouped).map(([type, items]) => (
				<div key={type} className="mb-3">
					<h5 className="text-xs font-medium text-gray-500 uppercase mb-1">{ELEMENT_TYPE_LABELS[type] ?? type}</h5>
					<div className="space-y-1">
						{items.map((el) => {
							const isEditing = editingId === el.elementId
							if (isEditing) {
								return (
									<div key={el.elementId} className="bg-white rounded border p-2 space-y-2">
										<div className="grid grid-cols-2 gap-2">
											<Input
												value={formData.name}
												onChange={(e) => setFormData({ ...formData, name: e.target.value })}
												className="h-7 text-sm"
											/>
											<Input
												value={formData.environment ?? ''}
												onChange={(e) => setFormData({ ...formData, environment: e.target.value })}
												className="h-7 text-sm"
												placeholder="Environment"
											/>
										</div>
										<Input
											value={formData.description ?? ''}
											onChange={(e) => setFormData({ ...formData, description: e.target.value })}
											className="h-7 text-sm"
											placeholder="Description"
										/>
										<Input
											value={formData.url ?? ''}
											onChange={(e) => setFormData({ ...formData, url: e.target.value })}
											className="h-7 text-sm"
											placeholder="URL"
										/>
										<div className="flex gap-1">
											<Button
												onClick={() => void handleUpdate()}
												size="sm"
												disabled={updateMutation.isPending}
												className="h-6 px-2 text-xs"
											>
												Save
											</Button>
											<Button onClick={resetForm} size="sm" variant="outline" className="h-6 px-2 text-xs">
												Cancel
											</Button>
										</div>
									</div>
								)
							}
							return (
								<div
									key={el.elementId}
									className="flex items-center justify-between bg-white rounded border px-3 py-1.5 text-sm"
								>
									<div className="flex items-center gap-3 min-w-0">
										<span className="font-medium truncate">{el.name}</span>
										{el.environment && (
											<Badge variant="outline" className="text-xs shrink-0">
												{el.environment}
											</Badge>
										)}
										{el.description && <span className="text-gray-500 truncate">{el.description}</span>}
									</div>
									<div className="flex items-center gap-2 shrink-0">
										{el.url && (
											<a
												href={el.url}
												target="_blank"
												rel="noopener noreferrer"
												className="text-xs text-indigo-600 hover:underline"
											>
												Open
											</a>
										)}
										{isSuperAdmin && (
											<>
												<button
													type="button"
													onClick={() => {
														setEditingId(el.elementId)
														setFormData({
															elementType: el.elementType,
															name: el.name,
															description: el.description ?? '',
															environment: el.environment ?? '',
															url: el.url ?? '',
														})
													}}
													className="text-xs text-indigo-600 hover:text-indigo-900"
												>
													Edit
												</button>
												<button
													type="button"
													onClick={() => void handleDelete(el.elementId)}
													className="text-xs text-red-600 hover:text-red-900"
												>
													Delete
												</button>
											</>
										)}
									</div>
								</div>
							)
						})}
					</div>
				</div>
			))}
		</div>
	)
}

export function ProjectsPage() {
	const { user } = useAuth()
	const isSuperAdmin = roleAtLeast(user?.role, UserRole.SUPER_ADMIN)
	const { data: clients = [] } = useClients()
	const {
		data: projects,
		isLoading,
		error,
		tempFilters,
		setTempFilters,
		applyFilters,
		clearFilters,
	} = useFilteredQuery<{
		projectId: string
		clientId: string
		subClientId: string | null
		code: string
		displayName: string
		description: string
		isActive: boolean
		ownerEmail: string
		pmEmail: string
		createdAt: string
	}>({
		endpoint: '/projects',
		queryKey: 'projects',
	})
	const createMutation = useCreateProject()
	const updateMutation = useUpdateProject()
	const deleteMutation = useDeleteProject()

	const [isAdding, setIsAdding] = useState(false)
	const [newRow, setNewRow] = useState({
		clientId: '',
		subClientId: '',
		code: '',
		displayName: '',
		description: '',
		ownerEmail: '',
		pmEmail: '',
	})
	const [editingId, setEditingId] = useState<string | null>(null)
	const [editingData, setEditingData] = useState<{
		clientId: string
		subClientId: string
		code: string
		displayName: string
		description: string
		ownerEmail: string
		pmEmail: string
	} | null>(null)
	const [expandedProjectId, setExpandedProjectId] = useState<string | null>(null)

	const clientMap = new Map(clients.map((c) => [c.clientId, c.name]))
	const parentClients = clients.filter((c) => c.parentClientId === null)
	const subClientsByParent = new Map<string, typeof clients>()
	for (const c of clients) {
		if (c.parentClientId) {
			const list = subClientsByParent.get(c.parentClientId) ?? []
			list.push(c)
			subClientsByParent.set(c.parentClientId, list)
		}
	}

	const handleSaveNew = async () => {
		if (!newRow.clientId || !newRow.code) return
		await createMutation.mutateAsync({
			clientId: newRow.clientId,
			subClientId: newRow.subClientId || null,
			code: newRow.code,
			displayName: newRow.displayName,
			ownerEmail: newRow.ownerEmail,
			pmEmail: newRow.pmEmail,
		})
		setIsAdding(false)
		setNewRow({
			clientId: '',
			subClientId: '',
			code: '',
			displayName: '',
			description: '',
			ownerEmail: '',
			pmEmail: '',
		})
	}

	const handleSaveEdit = async () => {
		if (!editingData || !editingId) return
		await updateMutation.mutateAsync({
			id: editingId,
			data: {
				clientId: editingData.clientId,
				subClientId: editingData.subClientId || null,
				code: editingData.code,
				displayName: editingData.displayName,
				description: editingData.description,
				ownerEmail: editingData.ownerEmail,
				pmEmail: editingData.pmEmail,
			},
		})
		setEditingId(null)
		setEditingData(null)
	}

	const handleDelete = async (id: string) => {
		if (!confirm('Are you sure you want to delete this project?')) return
		await deleteMutation.mutateAsync(id)
	}

	if (isLoading) return <div className="text-center py-8">Loading projects...</div>
	if (error) return <div className="text-center py-8 text-red-600">Error: {error.message}</div>

	return (
		<div>
			<div className="flex justify-between items-center mb-6">
				<h1 className="text-2xl font-bold text-gray-900">Projects</h1>
				{isSuperAdmin && (
					<Button onClick={() => setIsAdding(true)} disabled={isAdding} size="sm">
						Add Project
					</Button>
				)}
			</div>

			<FilterBar
				filters={[
					{
						key: 'clientId',
						label: 'Client',
						type: 'select',
						options: parentClients.map((c) => ({ label: c.name, value: c.clientId })),
					} satisfies FilterDefinition,
					{ key: 'code', label: 'Code', type: 'text' },
					{ key: 'displayName', label: 'Display Name', type: 'text' },
				]}
				values={tempFilters}
				onChange={setTempFilters}
				onApply={applyFilters}
				onClear={clearFilters}
			/>

			<div className="border rounded-lg">
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead className="w-8" />
							<TableHead>Client</TableHead>
							<TableHead>Sub-client</TableHead>
							<TableHead>Code</TableHead>
							<TableHead>Display Name</TableHead>
							<TableHead>Owner</TableHead>
							<TableHead>PM</TableHead>
							<TableHead>Status</TableHead>
							<TableHead className="text-right">Actions</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{isAdding && (
							<TableRow>
								<TableCell />
								<TableCell>
									<select
										value={newRow.clientId}
										onChange={(e) => setNewRow({ ...newRow, clientId: e.target.value, subClientId: '' })}
										className="border rounded px-2 py-1 text-sm w-full"
									>
										<option value="">Select client...</option>
										{parentClients.map((c) => (
											<option key={c.clientId} value={c.clientId}>
												{c.name}
											</option>
										))}
									</select>
								</TableCell>
								<TableCell>
									<select
										value={newRow.subClientId}
										onChange={(e) => setNewRow({ ...newRow, subClientId: e.target.value })}
										disabled={!newRow.clientId || (subClientsByParent.get(newRow.clientId)?.length ?? 0) === 0}
										className="border rounded px-2 py-1 text-sm w-full disabled:opacity-50"
									>
										<option value="">— None —</option>
										{(subClientsByParent.get(newRow.clientId) ?? []).map((c) => (
											<option key={c.clientId} value={c.clientId}>
												{c.name}
											</option>
										))}
									</select>
								</TableCell>
								<TableCell>
									<Input
										value={newRow.code}
										onChange={(e) => setNewRow({ ...newRow, code: e.target.value })}
										placeholder="project-code"
										className="font-mono"
									/>
								</TableCell>
								<TableCell>
									<Input
										value={newRow.displayName}
										onChange={(e) => setNewRow({ ...newRow, displayName: e.target.value })}
										placeholder="Display Name"
									/>
								</TableCell>
								<TableCell>
									<EmailAutocomplete
										value={newRow.ownerEmail}
										onChange={(email) => setNewRow({ ...newRow, ownerEmail: email })}
										placeholder="Owner email..."
									/>
								</TableCell>
								<TableCell>
									<EmailAutocomplete
										value={newRow.pmEmail}
										onChange={(email) => setNewRow({ ...newRow, pmEmail: email })}
										placeholder="PM email..."
									/>
								</TableCell>
								<TableCell>
									<Badge>Active</Badge>
								</TableCell>
								<TableCell className="text-right">
									<div className="flex items-center justify-end gap-1">
										<Button
											onClick={() => void handleSaveNew()}
											size="sm"
											disabled={createMutation.isPending}
											className="h-7 px-2 text-xs"
										>
											Save
										</Button>
										<Button onClick={() => setIsAdding(false)} size="sm" variant="outline" className="h-7 px-2 text-xs">
											Cancel
										</Button>
									</div>
								</TableCell>
							</TableRow>
						)}
						{projects.map((project) => {
							const isEditing = editingId === project.projectId
							const isExpanded = expandedProjectId === project.projectId
							return (
								<Fragment key={project.projectId}>
									<TableRow className={isExpanded ? 'border-b-0' : ''}>
										<TableCell className="w-8">
											<button
												type="button"
												onClick={() => setExpandedProjectId(isExpanded ? null : project.projectId)}
												className="text-gray-400 hover:text-gray-600"
											>
												{isExpanded ? '▼' : '▶'}
											</button>
										</TableCell>
										<TableCell>
											{isEditing ? (
												<select
													value={editingData?.clientId ?? ''}
													onChange={(e) =>
														setEditingData(
															editingData ? { ...editingData, clientId: e.target.value, subClientId: '' } : null,
														)
													}
													className="border rounded px-2 py-1 text-sm w-full"
												>
													{parentClients.map((c) => (
														<option key={c.clientId} value={c.clientId}>
															{c.name}
														</option>
													))}
												</select>
											) : (
												(clientMap.get(project.clientId) ?? project.clientId)
											)}
										</TableCell>
										<TableCell className="text-sm">
											{isEditing ? (
												<select
													value={editingData?.subClientId ?? ''}
													onChange={(e) =>
														setEditingData(editingData ? { ...editingData, subClientId: e.target.value } : null)
													}
													disabled={
														!editingData?.clientId || (subClientsByParent.get(editingData.clientId)?.length ?? 0) === 0
													}
													className="border rounded px-2 py-1 text-sm w-full disabled:opacity-50"
												>
													<option value="">— None —</option>
													{(subClientsByParent.get(editingData?.clientId ?? '') ?? []).map((c) => (
														<option key={c.clientId} value={c.clientId}>
															{c.name}
														</option>
													))}
												</select>
											) : project.subClientId ? (
												(clientMap.get(project.subClientId) ?? project.subClientId)
											) : (
												<span className="text-gray-400">—</span>
											)}
										</TableCell>
										<TableCell className="font-mono text-sm">
											{isEditing ? (
												<Input
													value={editingData?.code ?? ''}
													onChange={(e) =>
														setEditingData(editingData ? { ...editingData, code: e.target.value } : null)
													}
													className="font-mono"
												/>
											) : (
												<Link to={`/projects/${project.projectId}`} className="hover:text-blue-600">
													{project.code}
												</Link>
											)}
										</TableCell>
										<TableCell>
											{isEditing ? (
												<Input
													value={editingData?.displayName ?? ''}
													onChange={(e) =>
														setEditingData(editingData ? { ...editingData, displayName: e.target.value } : null)
													}
												/>
											) : (
												project.displayName || '-'
											)}
										</TableCell>
										<TableCell className="text-sm">
											{isEditing ? (
												<EmailAutocomplete
													value={editingData?.ownerEmail ?? ''}
													onChange={(email) =>
														setEditingData(editingData ? { ...editingData, ownerEmail: email } : null)
													}
													placeholder="Owner email..."
												/>
											) : (
												project.ownerEmail || '-'
											)}
										</TableCell>
										<TableCell className="text-sm">
											{isEditing ? (
												<EmailAutocomplete
													value={editingData?.pmEmail ?? ''}
													onChange={(email) => setEditingData(editingData ? { ...editingData, pmEmail: email } : null)}
													placeholder="PM email..."
												/>
											) : (
												project.pmEmail || '-'
											)}
										</TableCell>
										<TableCell>
											{project.isActive ? <Badge>Active</Badge> : <Badge variant="secondary">Inactive</Badge>}
										</TableCell>
										<TableCell className="text-right">
											{isSuperAdmin &&
												(isEditing ? (
													<div className="flex items-center justify-end gap-1">
														<Button
															onClick={() => void handleSaveEdit()}
															size="sm"
															disabled={updateMutation.isPending}
															className="h-7 px-2 text-xs"
														>
															Save
														</Button>
														<Button
															onClick={() => {
																setEditingId(null)
																setEditingData(null)
															}}
															size="sm"
															variant="outline"
															className="h-7 px-2 text-xs"
														>
															Cancel
														</Button>
													</div>
												) : (
													<div className="flex items-center justify-end gap-1">
														<Button
															onClick={() => {
																setEditingId(project.projectId)
																setEditingData({
																	clientId: project.clientId,
																	subClientId: project.subClientId ?? '',
																	code: project.code,
																	displayName: project.displayName ?? '',
																	description: project.description ?? '',
																	ownerEmail: project.ownerEmail ?? '',
																	pmEmail: project.pmEmail ?? '',
																})
															}}
															size="sm"
															variant="outline"
															className="h-7 px-2 text-xs"
														>
															Edit
														</Button>
														<Button
															onClick={() => void handleDelete(project.projectId)}
															size="sm"
															variant="destructive"
															disabled={deleteMutation.isPending}
															className="h-7 px-2 text-xs"
														>
															Delete
														</Button>
													</div>
												))}
										</TableCell>
									</TableRow>
									{isExpanded && (
										<TableRow key={`${project.projectId}-elements`}>
											<TableCell colSpan={9} className="p-0">
												<ProjectElementsSection projectId={project.projectId} isSuperAdmin={isSuperAdmin} />
											</TableCell>
										</TableRow>
									)}
								</Fragment>
							)
						})}
						{!isAdding && projects.length === 0 && (
							<TableRow>
								<TableCell colSpan={9} className="text-center py-8 text-gray-500">
									No projects found.
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</div>
		</div>
	)
}
